import { useMemo, useState } from 'react';
import { PiArrowDownBold, PiArrowUpBold, PiArrowsDownUpBold } from 'react-icons/pi';
import { cn } from '@v2/utils/cn';

export type SortDir = 'asc' | 'desc';

/**
 * Ordenação no cliente para tabelas de relatório: clique alterna asc/desc; números,
 * datas ISO e textos (pt-BR, ignorando acento) ordenam corretamente; vazios vão para o fim.
 */
export function useSortableRows<T>(rows: T[], accessors: Record<string, (row: T) => unknown>, initial: { key: string; dir: SortDir }) {
    const [sort, setSort] = useState(initial);

    const sorted = useMemo(() => {
        const get = accessors[sort.key];
        if (!get) return rows;
        const factor = sort.dir === 'asc' ? 1 : -1;

        return [...rows].sort((a, b) => {
            const va = get(a);
            const vb = get(b);
            const emptyA = va === null || va === undefined || va === '';
            const emptyB = vb === null || vb === undefined || vb === '';
            if (emptyA || emptyB) return emptyA === emptyB ? 0 : emptyA ? 1 : -1;
            if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * factor;

            return String(va).localeCompare(String(vb), 'pt-BR', { numeric: true, sensitivity: 'base' }) * factor;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, sort]);

    const toggle = (key: string) =>
        setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));

    return { sorted, sort, toggle };
}

export function SortTh({
    label,
    sortKey,
    sort,
    onSort,
    align = 'left',
}: {
    label: string;
    sortKey: string;
    sort: { key: string; dir: SortDir };
    onSort: (key: string) => void;
    align?: 'left' | 'right';
}) {
    const active = sort.key === sortKey;
    const Icon = !active ? PiArrowsDownUpBold : sort.dir === 'asc' ? PiArrowUpBold : PiArrowDownBold;

    return (
        <th className={cn('px-3 py-2.5', align === 'right' && 'text-right')} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
            <button
                type="button"
                onClick={() => onSort(sortKey)}
                className={cn(
                    'inline-flex items-center gap-1 uppercase tracking-wide hover:text-gray-900 dark:hover:text-zinc-100',
                    active && 'text-gray-900 dark:text-zinc-100',
                    align === 'right' && 'flex-row-reverse',
                )}
            >
                {label}
                <Icon className={cn('size-3', !active && 'opacity-40')} aria-hidden />
            </button>
        </th>
    );
}
